import { Injectable, signal, computed, effect, provideAppInitializer, inject } from '@angular/core'
import { Router, NavigationEnd } from '@angular/router'
import { filter, pairwise, startWith } from 'rxjs/operators'
import { EMPTY, Observable } from 'rxjs'

@Injectable({providedIn: 'root'})
export class NavigationService {
	private readonly STORAGE_KEY = 'navigation-history'
	private readonly MAX_HISTORY_SIZE = 50
	
	private historySignal = signal<string[]>([])
	public readonly history = computed(() => this.historySignal())
	private isNavigatingBack = false;

	constructor(private router: Router) {
		this.loadHistoryFromStorage()
		this.router.events.pipe(
			filter((e): e is NavigationEnd => e instanceof NavigationEnd),
			startWith(null),
			pairwise()
		).subscribe(([prev, curr]) => {
			if (this.isNavigatingBack) {
				this.isNavigatingBack = false
				return
			}
			if (!prev) return
			if (!curr) return
			const prevPath = this.getPath(prev.urlAfterRedirects)
			const currPath = this.getPath(curr.urlAfterRedirects)
			if (prevPath !== currPath) {
				this.historySignal.update(h => {
					// Prevent duplicates by checking if the URL is already in history
					const prevUrl = prev.urlAfterRedirects
					const lastEntry = h[h.length - 1]
					
					// Don't add if it's the same as the last entry (prevents duplicates)
					if (lastEntry === prevUrl) {
						return h
					}
					
					const newHistory = [...h, prevUrl]
					// Limit history size to prevent storage bloat
					if (newHistory.length > this.MAX_HISTORY_SIZE) {
						return newHistory.slice(-this.MAX_HISTORY_SIZE)
					}
					return newHistory
				})
				this.saveHistoryToStorage()
			}
		})
	}

	private getPath(url: string): string {
		return url.split('?')[0]
	}

	public back(): void {
		const stack = this.historySignal()
		const updated = [...stack]
		const previousUrl = updated.pop()
		this.historySignal.set(updated)
		this.saveHistoryToStorage()
		this.isNavigatingBack = true
		void this.router.navigateByUrl(previousUrl ?? '/')
	}

	/**
	 * Save navigation history to sessionStorage
	 */
	private saveHistoryToStorage(): void {
		try {
			const history = this.historySignal()
			sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(history))
		} catch (error) {
			console.warn('Failed to save navigation history to sessionStorage:', error)
		}
	}

	/**
	 * Load navigation history from sessionStorage
	 */
	private loadHistoryFromStorage(): void {
		try {
			const storedHistory = sessionStorage.getItem(this.STORAGE_KEY)
			if (storedHistory) {
				const history: string[] = JSON.parse(storedHistory)
				// Remove duplicates and clean up the history
				const cleanedHistory = this.removeConsecutiveDuplicates(history)
				this.historySignal.set(cleanedHistory)
			}
		} catch (error) {
			console.warn('Failed to load navigation history from sessionStorage:', error)
		}
	}

	/**
	 * Remove consecutive duplicates from history array
	 */
	private removeConsecutiveDuplicates(history: string[]): string[] {
		if (history.length === 0) return history
		
		return history.reduce((acc: string[], current: string, index: number) => {
			// Skip if it's the same as the previous entry
			if (index > 0 && current === history[index - 1]) {
				return acc
			}
			acc.push(current)
			return acc
		}, [])
	}

	/**
	 * Clear navigation history (useful for logout)
	 */
	public clearHistory(): void {
		this.historySignal.set([])
		try {
			sessionStorage.removeItem(this.STORAGE_KEY)
		} catch (error) {
			console.warn('Failed to clear navigation history from sessionStorage:', error)
		}
	}
}

function initialize(navigationService: NavigationService): () => Observable<any> {
	return () => EMPTY
}

export const initializeNavigationService = provideAppInitializer(() => {
	return initialize(inject(NavigationService))()
})